import React from 'react';
import { MenuItem, MenuProps, Select, SelectChangeEvent, SliderProps, styled, useTheme } from '@mui/material';
import { ReactComponent as ArrowDown } from './img/arrow-down.svg';

type StyledSliderProps = SliderProps & {
  height?: string;
};

const StyledSelect = styled(Select)<StyledSliderProps>(({ theme, height }) => ({
  borderRadius: '8px',
  height: height || '36px',
  borderColor: theme.colors.gray,
  '& .MuiSelect-select': {
    fontFamily: 'Mulish',
    borderRadius: '8px',
    padding: '10px 16px',
  },
  '& .MuiSelect-icon': {
    height: '16px',
    width: '16px',
    padding: '0px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  '& .MuiList-root': {
    boxShadow: 'unset',
  },
  '& .MuiSelect-selectMenu': {
    color: theme.colors.gray,
    backgroundColor: theme.colors.secondaryColor,
    boxShadow: 'unset',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '16px',
    lineHeight: '24px',
    '&:focus': {
      backgroundColor: theme.colors.secondaryColor,
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px!important',
    borderColor: theme.colors.gray,
    transition: theme.transitions.create(['border-color']),
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.colors.gray,
  },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.colors.gray,
  },
  '&.Mui-disabled .MuiSelect-select' : {
    cursor: 'not-allowed',
  },
  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.colors.error,
  },
}));

const menuProps: Partial<MenuProps> = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

export type DropdownDataProps = {
  value: number;
  label: string | number;
};

type SelectProps = {
  data: DropdownDataProps[],
  value?: number | number[] | null,
  onChange?: (event: SelectChangeEvent) => void,
  placeholder?: string,
  disabled?: boolean,
  error?: boolean,
  name?: string,
  height?: string,
};

export default function SelectEl({ data, value, onChange, placeholder, disabled, error, name, height }: SelectProps) {
  const theme = useTheme();
  return (
    <StyledSelect
      height={height || '36px'}
      sx={{
        fontFamily: 'Mulish',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '400',
        letterSpacing: '1px',
        backgroundColor: theme.colors.secondaryColor,
        '& .MuiSelect-select': {
          color: value === 0 ? theme.colors.gray : theme.colors.primaryTextColor,
        },
        '& .MuiSelect-icon path' : {
          stroke: theme.palette.text.secondary,
          
        },
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            boxShadow: 'unset',
            border: `1px solid ${theme.colors.primaryColor}`,
            marginTop: '10px',
            borderRadius: '8px',
            backgroundColor: theme.colors.secondaryColor,
            maxHeight: `calc(8 * ${height || '36px'} + 8px)`,
            '&::-webkit-scrollbar': {
              width: '14px',
              padding: '15px 0',
              transform: 'scale(0.5)',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0, 0, 0, 0)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(64, 108, 255, 0.2)',
              borderRadius: '20px',
              border: '4px solid rgba(0, 0, 0, 0)',
              backgroundClip: 'padding-box',
            },
            '& .Mui-selected': {
              backgroundColor: 'unset!important',
              color: theme.colors.primaryColor,
            },
            '& .MuiMenuItem-root': {
              backgroundColor: 'unset!important',
              transition: 'color 0.2s',
              fontFamily: 'Mulish',
            },
            '& .MuiMenuItem-root:hover': {
              color: theme.colors.primaryColor,
            },
          },
        },
        ...menuProps,
      }}
      value={value}
      IconComponent={ArrowDown}
      onChange={(e) => { onChange(e); }}
      disabled={disabled}
      error={error}
      name={name}
    >
      <MenuItem sx={{ display: 'none' }} value={0}>{placeholder}</MenuItem>
      {data.map((item, index) => (
        <MenuItem key={index} value={item.value}>{item.label}</MenuItem>
      ))}
    </StyledSelect>
  );
}